import React, { useCallback, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { FileIcon, PlusIcon } from "lucide-react";
import {
  getSigningStatusLabel,
  type ListCfrContractItem,
  useListCfrContracts,
  useGenerateBulkPresignDownloadLinks,
} from "@/lib/api/cfr";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { CommodityOptions } from "@/lib/api/commodity";
import { Button } from "@/components/ui/button";
import { CommonDestinationTimezone } from "@/lib/utils";
import type { Table } from "@tanstack/react-table";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3, getBlobFromS3 } from "@/lib/file";
import { downloadMultipleFilesAsZip } from "@/lib/browser/download";
import { MimeType } from "@/lib/api/utils";
import { IndeterminateCheckbox } from "./components/IndeterminateCheckbox";
import { RoutePath } from "@/section/router";

export interface CfrDocumentTemplateRow {
  id: string;
  created_at: string;

  commodity_type: string;
  crop_year: number;
  destination_id?: string | null;

  version_label?: string;
  document_id: string;

  MarketZone?: { id: string; dtn_market_zone: string; };
}

export function ContractListSection({
  onInstanceClick,
  cropYear,
}: { onInstanceClick: any; cropYear?: number }) {
  const { toast } = useToast();
  const listResult = useListCfrContracts({ crop_year: cropYear });
  const navigate = useNavigate();
  const generateBulkDownload = useGenerateBulkPresignDownloadLinks();
  const [isGeneratingBulkDownload, setIsGeneratingBulkDownload] = useState(false);

  const list_data = useMemo(() => {
    return listResult?.data?.data?.data || [];
  }, [listResult]);

  const isLoading = listResult.isLoading;

  const navigateToPreview = useCallback(
    (row: ListCfrContractItem, type: "sent" | "final") => {
      if (!row.cfr_document_id || !row.id) {
        return;
      }

      const pathTemplate =
        type === "sent"
          ? RoutePath.CfrDocumentsInstanceSentPreview
          : RoutePath.CfrDocumentsInstanceFinalPreview;

      const path = pathTemplate
        .replace(":templateId", row.cfr_document_id)
        .replace(":instanceId", row.id);

      navigate(path);
    },
    [navigate],
  );

  const columns = useMemo((): DataTableColumn<ListCfrContractItem>[] => [
    {
      id: "template_name",
      accessorKey: "template_name",
      accessorFn: (row) => row.DocumentTemplateInstance?.document_title || row.DocumentTemplateInstance?.DocumentTemplate?.title || "",
      header: ({ table }) => (
        <>
          <IndeterminateCheckbox
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
          />{' '}
          Template
        </>
      ),
      cell: ({ row, getValue }) => {
        return (
          <>
              <IndeterminateCheckbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />{' '}
            <span className="ml-4">
              {getValue() as any}
            </span>
          </>
        );
      },
      size: 400,
    },
    {
      id: "customer_id",
      accessorKey: "customer_id",
      accessorFn: (row) => row.Customer?.long_name || "",
      header: "Customer",
    },
    {
      id: "commodity_type",
      accessorKey: "commodity_type",
      header: "Commodity Type",
      accessorFn: (row) => {
        const commodity_type = row.CfrDocument?.commodity_type || "";
        return CommodityOptions.find(item => item.value === commodity_type)?.label || commodity_type;
      },
    },
    {
      id: "crop_year",
      accessorKey: "crop_year",
      header: "Crop Year",
      accessorFn: (row) => row.CfrDocument?.crop_year || "",
      cell: (info) => info.getValue() as string,
      aggregatedCell: () => "",
    },
    {
      id: "customer_destination_id",
      accessorKey: "customer_destination_id",
      header: "Destination",
      accessorFn: (row) => row.CfrDocument?.Destination?.long_name || "",
    },
    {
      id: "contract_number",
      accessorKey: "contract_number",
      accessorFn: (row) => row.DocumentTemplateInstance?.meta_data?.contract_number || "",
      header: "Contract Number",
      cell: (info) => (info.getValue() as string) || "",
    },
    {
      id: "signing_sessions_status",
      accessorKey: "signing_sessions_status",
      header: "Status",
      accessorFn: (row) => {
        const last_status = row.DocumentTemplateInstance?.SigningSession?.last_status || "";
        return getSigningStatusLabel(last_status);
      },
    },
    {
      id: "signing_sessions_is_test",
      accessorKey: "signing_sessions_is_test",
      header: "Is Test",
      accessorFn: (row) => row.DocumentTemplateInstance?.SigningSession?.is_sandbox ? "Yes"  : "No",
      cell: (info) => info.getValue(),
    },
    {
      id: "send_template",
      accessorKey: "send_template",
      header: "New Send",
      accessorFn: (row) => row.DocumentTemplateInstance?.SigningSession?.sent_document_id,
      cell: (info) => {
        const id = info.getValue() as string;
        return (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => {
              onInstanceClick({ id: info.row.original?.cfr_document_id || "", customer_id: info.row.original.customer_id })
            }}
            disabled={!id}
          >
            <span className="sr-only">Send New Template</span>
            <PlusIcon className="h-4 w-4" />
          </Button>
        )
      },
      enableFiltering: false,
    },
    {
      id: "sent_preview",
      accessorKey: "sent_preview",
      header: "Sent Document",
      accessorFn: (row) => row.DocumentTemplateInstance?.SigningSession?.sent_document_id,
      cell: (info) => {
        const id = info.getValue() as string;
        return (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => navigateToPreview(info.row.original, "sent")}
            disabled={!id}
          >
            <span className="sr-only">Preview Sent Document</span>
            <FileIcon className="h-4 w-4" />
          </Button>
        )
      },
      enableFiltering: false,
    },
    {
      id: "final_preview",
      accessorKey: "final_preview",
      header: "Final Document",
      accessorFn: (row) => row.DocumentTemplateInstance?.SigningSession?.final_document_id,
      cell: (info) => {
        const id = info.getValue() as string;
        return (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => navigateToPreview(info.row.original, "final")}
            disabled={!id}
          >
            <span className="sr-only">Preview Signed Document</span>
            <FileIcon className="h-4 w-4" />
          </Button>
        )
      },
      enableFiltering: false,
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => moment.tz(info.getValue(), CommonDestinationTimezone).format('YYYY-MM-DD HH:mm'),
      enableFiltering: true,
    },
  ], [navigateToPreview, onInstanceClick]);

  const generateBulkDownloadButton = ({ table }: { table: Table<ListCfrContractItem> }) => {
    return (
            <Button disabled={isGeneratingBulkDownload || table.getFilteredSelectedRowModel().rows.length === 0} variant="outline" onClick={async () => {
                try {
                    if (isGeneratingBulkDownload) {
                        return;
                    }
                    setIsGeneratingBulkDownload(true);

                    const selectedRows = table.getFilteredSelectedRowModel().rows;
                    const row_ids = selectedRows.map(row => row.original.id);

                    if (!row_ids.length) {
                        toast({
                            title: "No rows selected",
                            description: "",
                        })
                        return;
                    }

                    const result = await generateBulkDownload({ instance_ids: row_ids });
                    if (!result.success) {
                        throw result;
                    }

                    if (!result.data.presigned_urls?.length) {
                        toast({
                            title: `Found no contracts to download`,
                            description: "",
                            variant: "destructive",
                        });
                    }

                    const document_names = prefixDuplicateNames(result.data.document_names || []);
                    const downloadResults = await Promise.all(result.data.presigned_urls.map((presigned_url: string, index: number) => {
                        return getBlobFromS3(presigned_url);
                    }));

                    const downloadedResults = downloadResults.filter(result => result.success);
                    const errorResults = downloadResults.filter(result => !result.success);

                    if (errorResults.length) {
                        toast({
                            title: `Failed to download ${errorResults.length} contracts`,
                            description: "",
                            variant: "destructive",
                        });
                    }

                    await downloadMultipleFilesAsZip(downloadedResults.map((result, index: number) => {
                        return {
                            buffer: result.data,
                            fileName: index >= document_names.length ? `Cfr_Final_Contract_${index}.pdf` : appendPdfType(document_names[index] ?? `Cfr_Final_Contract_${index}.pdf`),
                            mimeType: MimeType.Pdf,
                        };
                    }), `Cfr_Final_Contracts_${moment().format("YYYY-MM-DD")}.zip`);

                } catch (error) {
                    console.error("Failed to generate bulk download", error);
                    toast({
                        title: "Failed to generate bulk download",
                        description: "",
                        variant: "destructive",
                    });
                } finally {
                    setIsGeneratingBulkDownload(false);
                }
            }}>
                Bulk Download Signed Documents
            </Button>
        );
    };

  return (
    <div className="flex flex-col gap-2">
      <DataTable
        stickyHeaders
        tableClassName="max-h-[calc(50vh-200px)] overflow-y-scroll"
        customToolbarButtons={[generateBulkDownloadButton]}
        data={list_data}
        columns={columns}
        enableColumnPinning
        enableMultiFilter
        enableColumnResizing
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        enableMultiSelect
        enableExport
        exportFilename={() => "cfr_instances_export.csv"}
        virtualizationRowHeight={65}
        virtualizationOverscan={2}
        enableVirtualization
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "created_at", desc: true }]}
        defaultFiltering={([{ id: "signing_sessions_is_test", value: ["No"] }])}
        emptyMessage={isLoading ? "Loading ..." : "No contracts found"}
      />
    </div>
  );
}

function appendPdfType(item: string) {
  if (item.endsWith(".pdf")) { return item; }
  return `${item}.pdf`;
}

function prefixDuplicateNames(names: string[]): string[] {
  const nameCounts = new Map<string, number>();
  const nameIndices = new Map<string, number>();
  
  // Count occurrences of each name
  for (const name of names) {
    nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
  }
  
  // Process names and prefix duplicates with index
  return names.map((name) => {
    const count = nameCounts.get(name) || 0;
    if (count > 1) {
      const currentIndex = (nameIndices.get(name) || 0) + 1;
      nameIndices.set(name, currentIndex);
      return `${currentIndex}_${name}`;
    }
    return name;
  });
}
